type Option = Record<string, any>;
const sharedListsKey = Symbol.for('fatetide.dynamic-select-option-lists.v1');

/** Separate browser bundles must use one store, including its invalidation lifecycle. */
export function sharedDynamicSelectOptionLists(): DynamicSelectOptionLists {
    const host = window as unknown as { [sharedListsKey]?: DynamicSelectOptionLists };
    return host[sharedListsKey] ??= new DynamicSelectOptionLists();
}

type Pending = {
    id: string; key: string; url: string; type: Record<string, unknown>;
    promise: Promise<Option[]>; resolve: (rows: Option[]) => void; reject: (error: unknown) => void;
};

/** Page-local, entitlement-keyed raw lists. Consumers copy before applying preselection. */
export class DynamicSelectOptionLists {
    private readonly cached = new Map<string, { key: string; at: number; rows: Option[] }>();
    private readonly pending = new Map<string, Pending>();
    private queue: Pending[] = [];
    private timer: ReturnType<typeof setTimeout> | undefined;

    constructor(private readonly send: typeof fetch = (...args) => fetch(...args)) {}

    load(url: string, key: string, type: Record<string, unknown>): Promise<Option[]> {
        const id = url + '\n' + key;
        const cached = this.cached.get(id);
        if (cached && Date.now() - cached.at <= 5 * 60 * 1000) return Promise.resolve(cached.rows);
        this.cached.delete(id);
        const pending = this.pending.get(id);
        if (pending) return pending.promise;
        let resolve!: Pending['resolve'], reject!: Pending['reject'];
        const promise = new Promise<Option[]>((yes, no) => { resolve = yes; reject = no; });
        const entry = { id, key, url, type: { ...type, preselected: [] }, promise, resolve, reject };
        this.pending.set(id, entry);
        this.queue.push(entry);
        // The endpoint already accepts multiple type requests. Combine sibling initializers,
        // including Tom Select's separate preload timers, without delaying interactive typing.
        this.timer ??= setTimeout(() => this.flush(), 10);
        return promise;
    }

    clear(prefix?: string): void {
        for (const [id, entry] of this.cached) if (!prefix || entry.key.startsWith(prefix)) this.cached.delete(id);
        for (const [id, entry] of this.pending) if (!prefix || entry.key.startsWith(prefix)) this.pending.delete(id);
    }

    private flush(): void {
        this.timer = undefined;
        const queue = this.queue; this.queue = [];
        const groups = new Map<string, Pending[]>();
        for (const entry of queue) {
            if (this.pending.get(entry.id) !== entry) { entry.reject(new Error('Option list invalidated')); continue; }
            const entries = groups.get(entry.url) ?? [];
            entries.push(entry); groups.set(entry.url, entries);
        }
        for (const [url, entries] of groups) {
            for (let i = 0; i < entries.length; i += 32) void this.fetchBatch(url, entries.slice(i, i + 32));
        }
    }

    private async fetchBatch(url: string, entries: Pending[]): Promise<void> {
        try {
            const response = await this.send(url, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ types: entries.map(entry => entry.type) }) });
            if (!response.ok) throw new Error('Option list HTTP ' + response.status);
            const data = await response.json();
            for (const entry of entries) {
                // Never borrow the first bucket: a batch can contain different entitlements.
                const rows = data?.[entry.key]?.options;
                if (this.pending.get(entry.id) !== entry) entry.reject(new Error('Option list invalidated'));
                else if (!Array.isArray(rows)) entry.reject(new Error('Missing option bucket: ' + entry.key));
                else { this.cached.set(entry.id, { key: entry.key, at: Date.now(), rows }); entry.resolve(rows); }
            }
        } catch (error) {
            for (const entry of entries) entry.reject(error);
        } finally {
            for (const entry of entries) if (this.pending.get(entry.id) === entry) this.pending.delete(entry.id);
        }
    }
}
